import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { customerDetailAPI } from '../../services/api';
import './Customers.css';

const CustomerDetailListPage = ({ user }) => {
  const navigate = useNavigate();
  const [customerDetails, setCustomerDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingDetail, setEditingDetail] = useState(null);

  useEffect(() => {
    fetchCustomerDetails();
  }, []);

  const fetchCustomerDetails = async () => {
    try {
      setLoading(true);
      const response = await customerDetailAPI.getAll();
      setCustomerDetails(response);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load custom fields');
      console.error('Error fetching customer details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingDetail(null);
    setShowForm(true);
  };

  const handleEdit = (detail) => {
    setEditingDetail(detail);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this custom field? All associated values will be lost.')) {
      return;
    }

    try {
      await customerDetailAPI.delete(id);
      fetchCustomerDetails();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete custom field');
      console.error('Error deleting customer detail:', err);
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingDetail(null);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingDetail(null);
    fetchCustomerDetails();
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-main-wrapper">
        <main className="dashboard-content">
          <div className="customer-list-container">
            <div className="customer-list-header">
              <h1>Custom Customer Fields</h1>
              <button
                className="btn btn-primary"
                onClick={handleAdd}
              >
                + Add Custom Field
              </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            {loading ? (
              <div className="loading">Loading custom fields...</div>
            ) : (
              <>
                {customerDetails.length === 0 ? (
                  <div className="empty-state">
                    <p>No custom fields configured yet.</p>
                    <p>Add custom fields to collect additional information from customers.</p>
                  </div>
                ) : (
                  <div className="customer-table-container">
                    <table className="customer-table">
                      <thead>
                        <tr>
                          <th>Field Name</th>
                          <th>Type</th>
                          <th>Required</th>
                          <th>Active</th>
                          <th>Sort Order</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {customerDetails.map((detail) => (
                          <tr key={detail.id}>
                            <td>{detail.name}</td>
                            <td>
                              <span className="badge badge-info">
                                {detail.field_type}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${detail.is_required ? 'badge-warning' : 'badge-secondary'}`}>
                                {detail.is_required ? 'Yes' : 'No'}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${detail.is_active ? 'badge-success' : 'badge-secondary'}`}>
                                {detail.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>{detail.sort_order}</td>
                            <td>
                              <div className="action-buttons">
                                <button
                                  className="btn btn-sm btn-warning"
                                  onClick={() => handleEdit(detail)}
                                >
                                  Edit
                                </button>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleDelete(detail.id)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {showForm && (
        <CustomerDetailForm
          detail={editingDetail}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

const CustomerDetailForm = ({ detail, onClose, onSuccess }) => {
  const isEditMode = Boolean(detail);

  const [formData, setFormData] = useState({
    name: detail?.name || '',
    field_type: detail?.field_type || 'text',
    options: detail?.options || [],
    is_required: detail?.is_required || false,
    sort_order: detail?.sort_order || 0,
    is_active: detail?.is_active !== undefined ? detail.is_active : true,
  });

  const [optionsText, setOptionsText] = useState(
    detail?.options ? detail.options.join('\n') : ''
  );

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'select', label: 'Select (Dropdown)' },
    { value: 'checkbox', label: 'Checkbox' },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleOptionsChange = (e) => {
    setOptionsText(e.target.value);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Field name is required';
    }

    if (formData.field_type === 'select' && !optionsText.trim()) {
      newErrors.options = 'Options are required for select fields';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const submitData = {
        ...formData,
        options: formData.field_type === 'select' 
          ? optionsText.split('\n').filter(opt => opt.trim())
          : null,
      };

      if (isEditMode) {
        await customerDetailAPI.update(detail.id, submitData);
      } else {
        await customerDetailAPI.create(submitData);
      }

      onSuccess();
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      }
      setSubmitError(
        err.response?.data?.message || 
        `Failed to ${isEditMode ? 'update' : 'create'} custom field`
      );
      console.error('Error submitting form:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? 'Edit Custom Field' : 'Add Custom Field'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {submitError && (
              <div className="error-message">{submitError}</div>
            )}

            <div className="form-group">
              <label htmlFor="name">
                Field Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? 'error' : ''}
                placeholder="e.g., Preferred Contact Time"
              />
              {errors.name && (
                <span className="error-text">{errors.name}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="field_type">
                Field Type <span className="required">*</span>
              </label>
              <select
                id="field_type"
                name="field_type"
                value={formData.field_type}
                onChange={handleChange}
              >
                {fieldTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {formData.field_type === 'select' && (
              <div className="form-group">
                <label htmlFor="options">
                  Options (one per line) <span className="required">*</span>
                </label>
                <textarea
                  id="options"
                  value={optionsText}
                  onChange={handleOptionsChange}
                  className={errors.options ? 'error' : ''}
                  placeholder="Morning&#10;Afternoon&#10;Evening"
                  rows="5"
                />
                {errors.options && (
                  <span className="error-text">{errors.options}</span>
                )}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="sort_order">Sort Order</label>
              <input
                type="number"
                id="sort_order"
                name="sort_order"
                value={formData.sort_order}
                onChange={handleChange}
                placeholder="0"
              />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="is_required"
                  checked={formData.is_required}
                  onChange={handleChange}
                />
                <span>Required Field</span>
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
                <span>Active</span>
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : isEditMode ? 'Update Field' : 'Create Field'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerDetailListPage;
